import React, {useContext} from 'react';
import {useSHCore, AjaxParam} from '@hooks/useSHCore';
import {getInitialComponentData} from "@helpers/HelperInitialPageState";
import {notify} from "@contexts/ContextNotificationProvider";
import {hasProperty} from "@helpers/HelperFunc";
import HelperGoogleAnalytics4 from "../../src/common/helpers/HelperGoogleAnalytics4";

const ContextCart = React.createContext({});


/**
 * @class ContextCartProvider
 * @property {array} contextData
 * @property {Function} isServiceInCart Дали дадената услуга е в количката.
 * @property {Function} isServiceTypeInCart If a service of the given type is in the cart.
 * @property {Function} isNewServiceInCart Дали дадената услуга е в количката. Проверява само между новите услуги
 * @property {Function} getNewDomainInCart Връща данните за даден домейн в количката. Глеад само новите услуги.
 * @property {Function} getNewServiceData Връща данните за дадена услуга в количката. Глеад само новите услуги.
 * @property {Function} getSelectedPriceForLineId Връща избраният service price id за даден ред.
 * @property {Function} removeFromCart Премахва услуга от количката
 * @property {Function} addDomainToCart Добавя домейн в количката
 * @property {Function} addServiceToCart Добавя услуга в количката по дадено servicePriceId
 * @property {Function} addSSToCart Добавя SuperStart услуга в количката по дадено servicePriceId
 * @property {Function} setContextData Задава данни в контекста.
 *
 */
const ContextCartProvider = ({children}) => {

	const providerData = getInitialComponentData('contextCart');

	/**
	 * Задава данни в контекста
	 *
	 * @param contextData
	 */
	const setContextData = (contextData) => {
		const state = core.state;
		state.contextData = contextData;
		core.setState(state);
	};

	/**
	 * Дали дадената услуга е в количката.
	 * @param {string} serviceId
	 * @returns {boolean}
	 */
	const isServiceInCart = (serviceId) => {
		let sIds = core.state.contextData.serviceIds;
		return sIds[serviceId] === serviceId;
	};

	const isServiceTypeInCart = (serviceType) => {
		const lines = core.state.contextData.lines;

		if (!lines.length) {
			return false;
		}

		return typeof lines.find(line => serviceType === line.serviceType) !== 'undefined';
	}

	/**
	 * Дали дадената услуга е в количката. Проверява само между новите услуги
	 * @param {string} serviceId
	 * @returns {boolean}
	 */
	const isNewServiceInCart = (serviceId) => {
		if (hasProperty(core.state.contextData, 'onlyNewServices')) {
			let sIds = core.state.contextData.onlyNewServices;
			return sIds[serviceId] === serviceId;
		} else {
			return false;
		}

	};
	/**
	 * Връща данните за даден домейн в количката. Глеад само новите услуги.
	 * @param {string} domainName
	 * @returns {[]}
	 */
	const getNewDomainInCart = (domainName) => {
		let domainLineData = [];
		const lines = core.state.contextData.lines;
		if (lines.length > 0) {
			domainLineData = lines.filter(line => line.serviceName === domainName && line.isNewService === true && line.serviceType === 'Domains');
		}
		return domainLineData;
	};
	/**
	 * Връща данните за дадена услуга в количката. Глеад само новите услуги.
	 * @param {string} serviceId
	 * @returns {[]}
	 */
	const getNewServiceData = (serviceId) => {
		let serviceData = [];
		const lines = core.state.contextData.lines;
		if (lines.length > 0) {
			serviceData = lines.filter(line => line.serviceId === serviceId && line.isNewService === true);
		}
		return serviceData;
	};

	/**
	 * Връща избраният service price id за даден ред.
	 * @param {string} lineId
	 * @returns {string}
	 */
	const getSelectedPriceForLineId = (lineId) => {
		return core.state.contextData.lineServicePriceMap[lineId];
	};
	/**
	 * Премахва услуга от количката
	 * @param {string} lineId
	 * @param {Function} [callBackSuccess]
	 */
	const removeFromCart = (lineId, callBackSuccess) => {
		const ajaxParam = new AjaxParam();
		ajaxParam.callbackSuccess = (data) => {
			if (core.context.get('ComponentCart')) {
				core.context.set('ComponentCart', data);
				core.context.forceUpdate('ComponentCart');
			}


			const state = core.state;
			state.contextData = data.componentData;

			core.eventRaise('ToggleCartDisabledState', {disable: false});
			notify.success(core.messages['SERVICE_REMOVE_FROM_CART_SUCCESS']);

			core.setState(state, () => {
				if (typeof callBackSuccess === 'function') {
					callBackSuccess(data);
				}
			});
			if (data.componentData.countServices === 0) {
				core.eventRaise('ToggleCart', {isOpen: false});
			}
		};
		ajaxParam.callbackError = ({componentErrors}) => {
			core.eventRaise('ToggleCartDisabledState', {disable: false});
			const errorMessage = componentErrors[0];
			notify.error(core.messages[errorMessage] || errorMessage);
		}

		ajaxParam.cypressParamString = '?ContextCart-ActionRemoveFromCart';
		core.ajaxCall('ActionRemoveFromCart', {lineId: lineId}, ajaxParam);
	};
	/**
	 * Добавя домейн в количката
	 * @param {string} domainName
	 * @param {string} servicePriceId
	 * @param {Function} [callBackSuccess]
	 */
	const addDomainToCart = (domainName, servicePriceId, callBackSuccess) => {
		const ajaxParam = new AjaxParam();
		ajaxParam.callbackSuccess = (data) => {
			if (core.context.get('ComponentCart')) {
				core.context.set('ComponentCart', data);
				core.context.forceUpdate('ComponentCart');
			}


			const state = core.state;
			state.contextData = data.componentData;

			core.eventRaise('ToggleCartDisabledState', {disable: false});
			notify.success(core.messages['SERVICE_ADD_TO_CART_SUCCESS']);

			core.setState(state, () => {
				if (typeof callBackSuccess === 'function') {
					callBackSuccess(data);
				}
			});
		};
		ajaxParam.callbackError = ({componentErrors}) => {
			core.eventRaise('ToggleCartDisabledState', {disable: false});
			const errorMessage = componentErrors[0];
			notify.error(core.messages[errorMessage] || errorMessage);
		}

		const actionData = {
			domainName: domainName,
			servicePriceId: servicePriceId
		};
		ajaxParam.cypressParamString = '?ContextCart-ActionAddDomainToCart';
		core.ajaxCall('ActionAddDomainToCart', actionData, ajaxParam);
	};

	/**
	 * Добавя услуга в количката по дадено ServicePriceId
	 * @param {string} servicePriceId
	 * @param {string} breadCrumbMode
	 * @param {Function} [callBackSuccess]
	 * @param {string} [promoCode]
	 */
	const addServiceToCart = (servicePriceId, breadCrumbMode, callBackSuccess, promoCode = '') => {
		const ajaxParam = new AjaxParam();
		ajaxParam.callbackSuccess = (data) => {
			// if (core.context.get('ComponentCartBig')) {
			// 	core.context.set('ComponentCartBig', data);
			// 	core.context.forceUpdate('ComponentCartBig');
			// } else if (core.context.get('ComponentCart')) {
			// 	core.context.set('ComponentCart', data);
			// 	core.context.forceUpdate('ComponentCart');
			// }

			const state = core.state;
			state.contextData = data.componentData;

			notify.success(core.messages['SERVICE_ADD_TO_CART_SUCCESS']);

			if (core.mounted) {
				core.setState(state, () => {
					if (typeof callBackSuccess === 'function') {
						callBackSuccess(data);
					}
				});
			} else {
				if (typeof callBackSuccess === 'function') {
					callBackSuccess(data);
				}
			}
		};

		const actionData = {
			servicePriceId: servicePriceId,
			breadCrumbMode: breadCrumbMode,
			promoCode: promoCode
		};

		ajaxParam.cypressParamString = '?ContextCart-ActionAddDServiceToCart';
		core.ajaxCall('ActionAddServiceToCart', actionData, ajaxParam);
	}

	const addSSToCart = (priceId, breadCrumbMode, promoCode = '') => {
		const ajaxParam = new AjaxParam()
		ajaxParam.isLoading = false
		core.ajaxCall('ActionAddSSToCart', {priceId, breadCrumbMode, promoCode}, ajaxParam)
	}

	/**
	 *
	 */
	const runAction = (action, actionData = {}, ajaxParam = null) => {
		if (!action || typeof action !== 'string') {
			return;
		}

		core.ajaxCall(action, actionData, ajaxParam);
	}

	/**
	 *
	 */
	const startShopikoTrial = () => {
		const label = 'Product Category/Online_shop - Product Variant/Trial';
		HelperGoogleAnalytics4.TrackEvent('Shopiko Events', 'Free trial - 14 days', label);

		const ajaxParam = new AjaxParam()
		ajaxParam.isLoading = false
		core.ajaxCall('ActionStartShopikoTrial', {}, ajaxParam);
	}

	/**
	 *
	 */
	const startSiteBuilderTrial = (serviceId, templateId = '') => {

		const ajaxParam = new AjaxParam()
		ajaxParam.isLoading = false

		const actionData = {
			planId: serviceId,
			templateId: templateId,
		}

		core.ajaxCall('ActionStartSiteBuilderTrial', actionData, ajaxParam);
	}

	/**
	 *
	 */
	const startWebnodeTrial = () => {
		const ajaxParam = new AjaxParam();
		ajaxParam.isLoading = false;

		core.ajaxCall('ActionStartWebnodeTrial', {}, ajaxParam);
	}

	/**
	 *
	 */
	const redirectToCart = () => {
		const ajaxParam = new AjaxParam()
		ajaxParam.isLoading = false

		core.ajaxCall('ActionContinue', {}, ajaxParam);
	}

	/**
	 * Добавя услуга в количката по дадено ServicePriceId
	 * @param {string} servicePriceId
	 * @param {number} count
	 * @param {string?} breadCrumbMode
	 * @param {Function?} [callBackSuccess]
	 */
	const addServiceToCartWithCount = (servicePriceId, count, breadCrumbMode, callBackSuccess) => {
		const ajaxParam = new AjaxParam();
		ajaxParam.callbackSuccess = (data) => {
			if (core.context.get('ComponentCartBig')) {
				core.context.set('ComponentCartBig', data);
				core.context.forceUpdate('ComponentCartBig');
			} else if (core.context.get('ComponentCart')) {
				core.context.set('ComponentCart', data);
				core.context.forceUpdate('ComponentCart');
			}


			const state = core.state;
			state.contextData = data.componentData;

			notify.success(core.messages['SERVICE_ADD_TO_CART_SUCCESS']);
			if (core.mounted) {
				core.setState(state, () => {
					if (typeof callBackSuccess === 'function') {
						callBackSuccess(data);
					}
				});
			} else {
				if (typeof callBackSuccess === 'function') {
					callBackSuccess(data);
				}
			}
		};
		const actionData = {
			servicePriceId: servicePriceId,
			breadCrumbMode: breadCrumbMode,
			count: count,
		};
		ajaxParam.cypressParamString = '?ContextCart-ActionAddDServiceToCart';
		core.ajaxCall('ActionAddServiceToCart', actionData, ajaxParam);
	}

	/**
	 * @param {string} domainName
	 */
	const addTransferDomain = (domainName, callBackSuccess = null, callBackError = null) => {
		const ajaxParam = new AjaxParam();

		ajaxParam.callbackSuccess = (data) => {
			if (core.context.get('ComponentCartBig')) {
				core.context.set('ComponentCartBig', data);
				core.context.forceUpdate('ComponentCartBig');
			} else if (core.context.get('ComponentCart')) {
				core.context.set('ComponentCart', data);
				core.context.forceUpdate('ComponentCart');
			}

			const state = core.state;
			state.contextData = data.componentData;

			core.setState(state, () => {
				if (typeof callBackSuccess === 'function') {
					callBackSuccess(data);
				}
			});
		};

		ajaxParam.callbackError = (errorData) => {
			const {componentErrors} = errorData;
			const errorMessage = componentErrors[0];

			if (typeof callBackError === 'function') {
				callBackError(errorMessage);
			}
		};

		const actionData = {
			domain_name: domainName
		};

		core.ajaxCall('ActionTransferDomainFormSubmit', actionData, ajaxParam);
	};

	const core = useSHCore({
		name: providerData.componentName,
		token: providerData.componentToken,
		initialState: {
			contextData: providerData.componentData,
			isServiceInCart,
			isNewServiceInCart,
			getNewDomainInCart,
			getNewServiceData,
			getSelectedPriceForLineId,
			removeFromCart,
			addDomainToCart,
			addServiceToCart,
			addSSToCart,
			addTransferDomain,
			runAction,
			startShopikoTrial,
			startSiteBuilderTrial,
			startWebnodeTrial,
			redirectToCart,
			addServiceToCartWithCount,
			setContextData,
			isServiceTypeInCart
		},
	});

	return (
	  <ContextCart.Provider value={core.state}>
		  {children}
	  </ContextCart.Provider>
	);
};

/**
 *
 * @returns {ContextCartProvider}
 */
export const useCartContext = () => useContext(ContextCart);

export {ContextCart, ContextCartProvider};

